import moment from "moment";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "../../../../Component/style/common_style";

interface SettingDate {
  livedate: string[];
  setLivedate: React.Dispatch<React.SetStateAction<string[]>>;
  setLivetime: React.Dispatch<React.SetStateAction<number[]>>;
  livetime: number[];
  setFocusDateIdx: React.Dispatch<React.SetStateAction<number>>;
  scheduleCounts?: Record<string, number>;
  onMonthChange?: (month: string) => void;
}

const Calendar = ({
  livedate, setLivedate, setLivetime, livetime, setFocusDateIdx,
  scheduleCounts = {}, onMonthChange,
}: SettingDate) => {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const year = currentDate.getFullYear();
  const monthIndex = currentDate.getMonth();
  const month = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  useEffect(() => { onMonthChange?.(month); }, [month, onMonthChange]);

  const selectDate = (date: string) => {
    if (livedate.length > 1) {
      if (date === livedate[0]) setFocusDateIdx(0);
      else if (date === livedate[1]) setFocusDateIdx(1);
      else { setLivedate([date]); setLivetime([]); setFocusDateIdx(0); }
      return;
    }
    if (livedate.length === 1) {
      const firstDate = livedate[0];
      if (date === firstDate) { setFocusDateIdx(0); return; }
      if (moment(date).diff(moment(firstDate), "days") === 1) {
        if (livetime.length === 0) {
          setLivedate([date]); setLivetime([]); setFocusDateIdx(0);
        } else {
          setLivedate([firstDate, date]); setFocusDateIdx(1);
        }
        return;
      }
    }
    setLivedate([date]); setLivetime([]); setFocusDateIdx(0);
  };

  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const previousMonthDays = new Date(year, monthIndex, 0).getDate();
  const today = moment().format("YYYY-MM-DD");
  const monthTotal = Object.entries(scheduleCounts)
    .filter(([date]) => date.startsWith(`${month}-`))
    .reduce((total, [, count]) => total + count, 0);

  return (
    <Container>
      <MonthNavigation aria-label="달력 월 이동">
        <MonthButton type="button" aria-label="이전달" onClick={() => setCurrentDate(new Date(year, monthIndex - 1, 1))}>
          <Icon src={require("../../../../Image/Admin/ic_paging_left.png")} alt="" />
        </MonthButton>
        <MonthHeading><strong>{year}년 {monthIndex + 1}월</strong><MonthCount>라이브 {monthTotal}건</MonthCount></MonthHeading>
        <MonthButton type="button" aria-label="다음달" onClick={() => setCurrentDate(new Date(year, monthIndex + 1, 1))}>
          <Icon src={require("../../../../Image/Admin/ic_paging_right.png")} alt="" />
        </MonthButton>
      </MonthNavigation>
      <Weekdays>{["일", "월", "화", "수", "목", "금", "토"].map(day => <span key={day}>{day}</span>)}</Weekdays>
      <DateGrid>
        {Array.from({ length: 42 }, (_, index) => {
          const dayNumber = index - firstWeekday + 1;
          const outside = dayNumber < 1 || dayNumber > daysInMonth;
          const displayDay = dayNumber < 1 ? previousMonthDays + dayNumber : dayNumber > daysInMonth ? dayNumber - daysInMonth : dayNumber;
          if (outside) return <OutsideDay key={index}>{displayDay}</OutsideDay>;
          const date = `${month}-${String(dayNumber).padStart(2, "0")}`;
          const past = date < today;
          const count = scheduleCounts[date] || 0;
          return (
            <DateButton key={date} type="button" disabled={past}
              $selected={livedate.includes(date)} aria-pressed={livedate.includes(date)}
              aria-label={`${date} ${past ? "예약불가" : "예약가능"}, 라이브 ${count}건`}
              onClick={() => selectDate(date)}>
              <DayNumber>{dayNumber}</DayNumber>
              <Availability><span className="availability-prefix">예약</span>{past ? "불가" : "가능"}</Availability>
              {count > 0 && <CountBadge><span>라이브 </span>{count}건</CountBadge>}
            </DateButton>
          );
        })}
      </DateGrid>
    </Container>
  );
};

export default Calendar;

const Container = styled.div`width: 100%; min-width: 0;`;
const MonthNavigation = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 20px;
  min-height: 54px; margin-bottom: 14px;
`;
const MonthButton = styled.button`
  display: grid; place-items: center; width: 36px; height: 36px; padding: 0;
  border: 1px solid var(--admin-theme-border, #e8e1f6); border-radius: 10px; background: var(--admin-theme-surface, #fff); cursor: pointer;
  img { width: 17px; height: 17px; filter: var(--admin-theme-icon-filter, none); }
  &:hover { background: var(--admin-theme-accent-soft, #f5f0ff); }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
const MonthHeading = styled.div`
  display: flex; align-items: center; gap: 12px; color: var(--admin-theme-text, #2b2535);
  strong { font-size: 21px; font-weight: 700; }
  @media (max-width: 560px) { flex-direction: column; gap: 2px; strong { font-size: 18px; } }
`;
const MonthCount = styled.span`
  padding: 5px 9px; border-radius: 999px; background: var(--admin-theme-accent-soft, #f0eaff);
  color: var(--admin-theme-link, #6035cc); font-size: 12px; font-weight: 700; white-space: nowrap;
`;
const Weekdays = styled.div`
  display: grid; grid-template-columns: repeat(7, minmax(0, 1fr));
  height: 36px; align-items: center; text-align: center; color: var(--admin-theme-muted, #817a8c);
  font-size: 13px; font-weight: 600;
  span:first-child { color: var(--admin-theme-danger, #d15d67); }
  span:last-child { color: var(--admin-theme-link, #547cbe); }
`;
const DateGrid = styled.div`
  display: grid; grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-template-rows: repeat(6, minmax(0, 1fr)); height: 540px;
  overflow: hidden; border: 1px solid var(--color-border-default); border-radius: var(--radius-lg);
  background: var(--color-border-default); gap: 1px;
  @media (max-width: 760px) { height: min(80vw, 480px); min-height: 360px; }
`;
const OutsideDay = styled.div`
  padding: var(--space-2); background: var(--color-bg-page); color: var(--color-text-disabled); font-size: var(--text-caption);
  @media (max-width: 560px) { padding: 6px; }
`;
const DateButton = styled.button<{ $selected: boolean }>`
  display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-1);
  min-width: 0; padding: var(--space-2); border: 0; background: ${p => p.$selected ? "var(--color-brand-subtle)" : "var(--color-bg-surface)"};
  color: ${p => p.$selected ? "var(--color-text-link)" : "var(--color-text-primary)"}; text-align: left; cursor: pointer;
  box-shadow: ${p => p.$selected ? "inset 0 0 0 2px var(--color-brand)" : "none"};
  &:hover:not(:disabled) { background: var(--color-brand-subtle); }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: -2px; z-index: 1; }
  @media (max-width: 560px) { padding: var(--space-1); gap: 0; }
`;
const DayNumber = styled.strong`font-size: var(--text-body-lg); line-height: 1.2;`;
const Availability = styled.span`
  display: inline-flex; max-width: 100%; padding: var(--space-1);
  border-radius: var(--radius-sm); background: var(--color-success-subtle); color: var(--color-success);
  font-size: var(--text-caption); font-weight: 500; line-height: 1.2; white-space: nowrap;
  button:disabled & { background: transparent; color: var(--color-text-secondary); }
  @media (max-width: 560px) { padding: 0; .availability-prefix { display: none; } }
`;
const CountBadge = styled.span`
  padding: 0 var(--space-1); border-radius: var(--radius-sm); background: var(--color-brand-subtle);
  color: var(--color-text-link); font-size: var(--text-caption); font-weight: 600; line-height: 1.2; white-space: nowrap;
  button:disabled & { background: var(--color-bg-subtle); color: var(--color-text-secondary); }
  @media (max-width: 560px) { padding: 0; span { display: none; } }
`;
